<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useClassStore, useSessionStore, useUserStore } from '../../../stores';
import { Session, VocabRecordType, VocabResult, VocabResultDecision } from '../../../types/session';
import AudioPlayer from '../../General/AudioPlayer.vue';
import AllowSpeaking from './AllowSpeaking.vue';
import { useI18n } from 'petite-vue-i18n';
import { TEXT } from '../../../configs/text';
import { UserRole } from '../../../types/user';
import AudioRecorder from './AudioRecorder.vue';
import VocabResultDetails from './VocabResultDetails.vue';
import Button from '../../General/Button.vue';
import HighlightedWords from '../../Assessment/HighlightedWords.vue';

const user = useUserStore()
const classStore = useClassStore()
const sessionStore = useSessionStore()
const vocabItem = computed(() => {
    const v = sessionStore.classContent?.[Session.vocab]
    if (Array.isArray(v)) {
        return v.find(item => item.id === sessionStore.currentVocabId)
    }
    return null
})

const blob = ref<Blob | null>(null)
const vocabRecordAttempts = computed<VocabResult[]>(() => sessionStore.vocabRecordAttempts?.[classStore.roomName]?.[user.username || '']?.[sessionStore.currentVocabId]?.[sessionStore.vocabRecordingType] || [])
const currentAttemptIndex = ref<number | null>(resetAttemptIndex())
const maxAttempt = 3

const isAllowRecord = computed<boolean>(() => {
    if (user.userRole === UserRole.teacher) return true;
    if (user.userRole === UserRole.student && user.userInfo?.id && sessionStore.allowSpeakingStudentIds.includes(user.userInfo.id)) return true;
    return false;
})

const isRecordable = computed<boolean>(() => sessionStore.recordAttemptCount < maxAttempt)
const isSubmitted = ref<boolean>(false)

function handleSelectRecordAttempt(index: number) {
    if (!vocabRecordAttempts.value[index]) return;
    currentAttemptIndex.value = index
}

function resetAttemptIndex(): number | null {
    return vocabRecordAttempts.value.length > 0 ? vocabRecordAttempts.value.length - 1 : null
}

async function handleRecordingSubmit() {
    if (!blob.value) return;
    if (!vocabItem.value?.word) return;

    await sessionStore.submitAudioVocab(vocabItem.value.word, blob.value, sessionStore.vocabRecordingType)
    isSubmitted.value = true
    currentAttemptIndex.value = resetAttemptIndex()
}

watch(blob, () => {
    if (blob.value) return;
    isSubmitted.value = false
})

watch(() => sessionStore.currentVocabId, () => {
    blob.value = null
    currentAttemptIndex.value = resetAttemptIndex()
})

watch(() => sessionStore.vocabRecordingType, () => {
    blob.value = null
    currentAttemptIndex.value = resetAttemptIndex()
})

onBeforeUnmount(() => {
    blob.value = null
    currentAttemptIndex.value = null
})

function getTextColor(decision: VocabResultDecision): 'main' | 'secondary' | 'success' | 'info' | 'danger' | 'warning' | 'gray' | 'light' | 'white' {
    switch (decision) {
        case VocabResultDecision.correct:
            return 'success';
        case VocabResultDecision.warning:
            return 'warning';
        case VocabResultDecision.incorrect:
            return 'danger';
        default:
            return 'light';
    }
}

const { t } = useI18n()
</script>

<template>
    <div class="flex flex-col gap-3 h-full w-full">
        <div class="flex-1 flex flex-col items-center overflow-y-auto w-full overflow-x-hidden" v-if="vocabItem?.id">
            <!-- image -->
            <img v-if="vocabItem?.illustrationUrl" :src="vocabItem.illustrationUrl" class="max-h-[40%] mt-3" />

            <div class="w-full grid grid-cols-1  md:grid-cols-2 gap-5 mt-3 p-3">
                <!-- word -->
                <div class="flex flex-col items-center w-full">
                    <div class="relative w-full flex flex-1 flex-col gap-3 items-center justify-center text-center border border-neutral-200 border-b-4 lg:border-b-8 rounded-xl p-3 bg-neutral-100 transition-all"
                        :class="{ '!bg-white': sessionStore.vocabRecordingType == VocabRecordType.word }" @click="sessionStore.vocabRecordingType = VocabRecordType.word">
                        <span class="text-yellow-500 text-2xl lg:text-3xl font-bold">{{ vocabItem?.word }}</span>
                        <span class="lg:text-lg text-neutral-500">{{ vocabItem?.word_phonetic }}</span>
                        <div class="absolute bottom-0 right-0 translate-x-[25%] translate-y-[25%]" v-if="vocabItem?.vocabVoiceUrl">
                            <AudioPlayer class="!rounded-lg !bg-sky-400 !px-1 !py-1" :bubles="false" :src="vocabItem.vocabVoiceUrl" :key="vocabItem.vocabVoiceUrl" />
                        </div>
                    </div>
                </div>

                <!-- example -->
                <div class="flex flex-col items-center w-full">
                    <div v-if="vocabItem?.contentExample || vocabItem?.exampleVoiceUrl"
                        class="relative w-full flex flex-1 flex-col gap-3 items-center justify-center text-center border border-neutral-200 border-b-4 lg:border-b-8 rounded-xl p-3 bg-neutral-100 transition-all"
                        :class="{ '!bg-white': sessionStore.vocabRecordingType == VocabRecordType.example }" @click="sessionStore.vocabRecordingType = VocabRecordType.example">
                        <HighlightedWords :vocab="vocabItem" v-if="vocabItem?.contentExample"></HighlightedWords>
                        <div class="absolute bottom-0 right-0 translate-x-[25%] translate-y-[25%]" v-if="vocabItem?.exampleVoiceUrl">
                            <AudioPlayer class="!rounded-lg !bg-sky-400 !px-1 !py-1" :bubles="false" :src="vocabItem.exampleVoiceUrl" :key="vocabItem.exampleVoiceUrl" />
                        </div>
                    </div>
                </div>

                <!-- record and result-->
                <div class="flex flex-col items-center w-full" :class="{ 'md:col-start-1': sessionStore.vocabRecordingType != VocabRecordType.word, 'md:col-start-2': sessionStore.vocabRecordingType != VocabRecordType.word }">
                    <template v-if="isAllowRecord">
                        <AudioRecorder v-model="blob" :key="`${vocabItem.id}_${sessionStore.vocabRecordingType}`" :disabled="!isRecordable" :submitted="isSubmitted" @submit="handleRecordingSubmit" class="mt-6" />
                        <div class="text-center mt-2">
                            <div class="text-sm">
                                {{ t(TEXT.attempts_remaining, {
                                    current_attempt: maxAttempt - sessionStore.recordAttemptCount,
                                    max_attempt: maxAttempt
                                }) }}
                            </div>
                        </div>
                    </template>
                    <template v-if="currentAttemptIndex !== null">
                        <div class="flex items-center justify-center gap-2 my-3">
                            <div class="flex flex-col items-center gap-2" v-for="(attempt, index) in maxAttempt">
                                <Button :border="false" class="!p-2 size-10 !text-black" :color="index <= vocabRecordAttempts.length - 1 ? getTextColor(vocabRecordAttempts[index].decision) : 'light'" @click="handleSelectRecordAttempt(index)">
                                    {{ attempt }}
                                </Button>
                                <div class=" w-4/5 h-[3px] rounded-full " :class="{ 'bg-sky-500': index === currentAttemptIndex }"></div>
                            </div>
                        </div>
                    </template>
                </div>

                <div class="md:col-span-2">
                    <VocabResultDetails v-if="currentAttemptIndex !== null" :result="vocabRecordAttempts[currentAttemptIndex]" :key="`${vocabItem.id}${currentAttemptIndex}`" />
                </div>
            </div>


        </div>
        <AllowSpeaking v-if="user.userRole === UserRole.teacher" />
    </div>
</template>